import {
  FRAME_STYLE,
  getFrameNameFontSize,
} from "@excalidraw/excalidraw/constants";
import { MessageCircle } from "lucide-react";

import type { ReactNode } from "react";
import type { ExcalidrawElement } from "@excalidraw/excalidraw/element/types";

export type SealedFrameBadgeProps = {
  frame: ExcalidrawElement;
  commentCount: number;
  /** Scene coordinates to screen pixels — same convention the rest of the
   * canvas overlay uses (`screenX = (sceneX + scrollX) * zoom`). */
  toScreen(x: number, y: number): { x: number; y: number };
  /** Canvas zoom; the badge shrinks with zoom-out by the frame title's ratio. */
  zoom: number;
  onOpenComments(): void;
  /** Extra controls after the comment button (the student's 자랑하기). */
  children?: ReactNode;
};

/**
 * Sits in the frame's top-right corner, shrinking with zoom-out by the same
 * ratio as the frame title (full size at 100% and above) — a small always-visible "마감" mark plus a button that opens the
 * comment thread in a modal, instead of the pane this used to be (Task:
 * 마감 표시 + 댓글 모달). Anchored to the frame's own right edge rather than
 * the end of its title text, so it never depends on measuring that text. It
 * never joins `elements`, so it never appears in an export or a print (see
 * the print rule in styles.scss).
 */
export const SealedFrameBadge = ({
  frame,
  commentCount,
  toScreen,
  zoom,
  onOpenComments,
  children,
}: SealedFrameBadgeProps) => {
  const rightEdge = toScreen(frame.x + frame.width, frame.y);
  const scale = getFrameNameFontSize(zoom) / FRAME_STYLE.nameFontSize;

  return (
    <div
      className="canvaschool-frame-badge"
      style={{
        left: `${rightEdge.x}px`,
        top: `${rightEdge.y - FRAME_STYLE.nameOffsetY}px`,
        // Anchors the badge's bottom-right corner, not its top-left, to the
        // frame's top-right corner — the same edge the native title sits
        // just above of. Scaling about that same corner keeps it anchored.
        transform: `translate(-100%, -100%) scale(${scale})`,
        transformOrigin: "100% 100%",
      }}
    >
      <span className="canvaschool-frame-seal-label">마감</span>
      <button
        type="button"
        className="canvaschool-comment-button"
        aria-label={`댓글 ${commentCount}개 보기`}
        onClick={onOpenComments}
      >
        <MessageCircle size={14} aria-hidden="true" />
        <span aria-hidden="true">댓글</span>
        <span className="canvaschool-comment-count" aria-hidden="true">
          {commentCount}
        </span>
      </button>
      {children}
    </div>
  );
};
